// /events/:eventId — event detail, plus the organizer's close and delete controls.
//
// The close panel is where the type registry becomes visible to the user:
//   • An `auto_at_end` type (Duration) gets no close control at all, because the
//     server rejects a manual close on it.
//   • A `has_results` type (Threshold) gets the winners picker, and the copy says
//     plainly that closing with nobody named announces nothing — the ended post
//     IS the results announcement.
//   • Choosing "Canceled" hides the winners picker, because a canceled close
//     cannot record winners and the server returns CANCELED_NO_WINNERS if one
//     is sent anyway.

import {
  ApiClientError,
  closeEvent,
  deleteEvent,
  fetchEvent,
  listMembers,
  listRsvps,
} from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  eventType,
  eventTypeParticipatedViaFasting,
  fmtInstant,
  GroupRole,
  rsvpStatusLabel,
  splitEven,
  splitPot,
  TimeFormat,
  winnersLabel,
  type CloseReason,
  type ClosePlace,
  type EventDetail,
  type GroupMember,
  type RsvpCounts,
  type RsvpEntry,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { statusBadge, untilLabel, whenLabel } from './eventCard.js';
import { rsvpControl } from './rsvpControl.js';
import { placeRows } from './placeFields.js';

export const eventView: View = async (outlet, ctx) => {
  const eventId = Number(ctx.params.eventId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!Number.isInteger(eventId) || eventId <= 0) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid event.'));
    return;
  }

  let event: EventDetail;
  let zone: string;
  try {
    [event, zone] = await Promise.all([fetchEvent(eventId), viewerZone()]);
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    const msg =
      err instanceof ApiClientError && err.status === 404
        ? 'That event does not exist.'
        : err instanceof ApiClientError && err.status === 403
          ? 'You are not a member of this event’s group.'
          : 'Could not load this event.';
    main.replaceChildren(el('p', { class: 'form-error' }, msg));
    return;
  }

  const def = eventType(event.eventType);
  const canManage = event.myRole >= GroupRole.EVENTS_MANAGER;

  const heading = el('h1', {}, [
    el('span', { class: 'event-icon', title: def.label }, def.icon),
    ' ',
    event.title,
  ]);
  const badge = statusBadge(event);
  if (badge) heading.append(' ', badge);

  // The roster is rebuilt in place after any RSVP change, so the organizer sees
  // their own answer land in the list without a reload. A plain div, not a card
  // of its own: it lives inside the one summary card below.
  const roster = el('div', { class: 'stack-part' }, [el('p', { class: 'muted' }, 'Loading responses')]);
  const reloadRoster = () => {
    void listRsvps(event.eventId)
      .then((data) => roster.replaceChildren(...rosterChildren(data.rsvps, zone)))
      .catch(() =>
        roster.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the responses.')),
      );
  };

  const sections: HTMLElement[] = [
    // Name the group. This page is reached from a feed card, a notification, or
    // a day-of SMS as often as from the group's own event list, and nothing else
    // on screen said which group the event belonged to.
    el('p', { class: 'crumb' }, [
      el('a', { href: `/groups/${event.groupId}` }, event.groupName),
      ' · ',
      el('a', { href: `/groups/${event.groupId}/events` }, 'Events'),
    ]),
    heading,
    el('nav', { class: 'subnav' }, [
      el('a', { href: `/events/${event.eventId}/chat` }, 'Chat'),
      // GAME events only. A tournament is a table-assignment tool, so offering
      // it on a fast or a workout would be offering something meaningless --
      // and the family check comes from the type registry rather than a list of
      // type keys here, so a game leaf added later inherits the link for free.
      ...(def.family === 'game'
        ? [el('a', { href: `/events/${event.eventId}/tournament` }, 'Tournament')]
        : []),
      // Same shape, same reason: the flag is on the registry, so a second
      // fasting-shaped type would inherit this link without touching this file.
      ...(eventTypeParticipatedViaFasting(event.eventType)
        ? [el('a', { href: `/groups/${event.groupId}/fasting` }, 'Fasting')]
        : []),
      // Just "Group feed" — the breadcrumb directly above already names the
      // group and links to it, so repeating it here says it twice in three lines.
      el('a', { href: `/groups/${event.groupId}` }, 'Group feed'),
    ]),
    // Above the content, for the same reason the group feed puts it there: it is
    // the explanation for every control below being absent or refused.
    ...(event.isSuspended
      ? [
          el(
            'p',
            { class: 'notice notice-suspended' },
            `${event.groupName} is suspended by a site administrator. You can still ` +
              'read everything here, but nobody can RSVP, post, or change this event ' +
              'until it is restored.',
          ),
        ]
      : []),
    // ⚠ A SUBNAV LINK ALONE IS NOT ENOUGH HERE, which is the difference between
    // this and the Tournament link. Will's words: "they are not going to know to
    // go to the fasting tab" -- and a fast EVENT is only ever a date the group
    // aims at, so the event page is the whole of what a member sees and it asks
    // them to do something that happens somewhere else. The tab is for people
    // who already know; this line is for everybody else.
    //
    // It does NOT branch on whether they are already fasting. That would cost a
    // second request on every event page to personalise one sentence, and the
    // fasting page itself already renders the right thing either way -- a dial
    // if they are running, a Start card if they are not.
    ...(eventTypeParticipatedViaFasting(event.eventType)
      ? [
          el('section', { class: 'card fast-prompt' }, [
            el(
              'p',
              {},
              'Everyone starts in their own time. This date is the target, and your ' +
                'own clock starts when you say it does.',
            ),
            el('p', { class: 'small' }, [
              el(
                'a',
                { href: `/groups/${event.groupId}/fasting` },
                'Start your fast, or check on the group',
              ),
            ]),
          ]),
        ]
      : []),
    // One box, not three. The details, the RSVP control and the responses are
    // the same question asked three ways — "what is this and am I in?" — and
    // three bordered cards stacked down the page made the page mostly borders.
    el('section', { class: 'card event-summary' }, [
      ...detailsRows(event, zone),
      ...rsvpRows(event, zone, reloadRoster),
      roster,
    ]),
  ];

  if (event.winners.length > 0 || event.highHand) sections.push(winnersCard(event));
  // No organizer card while the group is suspended: every action on it — close,
  // edit — is refused by the server, and a button that errors on tap is worse
  // than no button. The notice above says why.
  if (canManage && !event.isSuspended) {
    sections.push(organizerCard(event, def.closeMode === 'manual'));
  } else if (
    // Not an organizer, but in the game: whoever is left at the end may finish it
    // with the winners. Completed only; the server refuses a cancel from them.
    !canManage &&
    !event.isSuspended &&
    !event.closedAt &&
    def.hasResults &&
    def.closeMode === 'manual' &&
    event.myStatus === 'yes'
  ) {
    sections.push(el('section', { class: 'card' }, [closePanel(event, true)]));
  }

  main.replaceChildren(...sections);
  reloadRoster();
};

// --- Read-only cards ------------------------------------------------------

function detailsRows(event: EventDetail, zone: string): HTMLElement[] {
  const rows: HTMLElement[] = [
    field('When', whenLabel(event, zone)),
  ];

  const until = untilLabel(event, zone);
  if (until) rows.push(field('Ends', until));

  // Worth surfacing: it explains why the time above may not be what the
  // organizer typed. An inherited zone is not interesting; an explicit one is.
  if (event.timeZoneIsExplicit && event.timeZone !== zone) {
    rows.push(field('Authored in', event.timeZone));
  }

  rows.push(...placeRows(event.location, event, field));
  if (event.description) rows.push(field('Details', event.description));
  if (event.feedMessage) rows.push(field('Note to members', event.feedMessage));
  if (event.creatorName) rows.push(field('Organizer', event.creatorName));
  if (event.seriesId !== null) rows.push(field('Repeats', 'One occurrence of a repeating event'));
  rows.push(
    field(
      'Announced',
      event.announcedAt ? 'Members were notified' : 'Not announced yet',
    ),
  );

  return [el('div', { class: 'event-fields' }, rows)];
}

function field(label: string, value: string | (string | HTMLElement)[]): HTMLElement {
  return el('p', {}, [el('strong', {}, `${label}: `), ...(Array.isArray(value) ? value : [value])]);
}

/**
 * RSVP controls plus the headcount.
 *
 * The counts are built from the event payload rather than fetched, because the
 * detail response already carries every number the control needs. The control
 * takes over from there and never refetches: each save response brings fresh
 * counts with it.
 */
function rsvpRows(
  event: EventDetail,
  viewerZone: string,
  onChange: () => void,
): HTMLElement[] {
  const rows: HTMLElement[] = [
    el('h2', {}, 'Your RSVP'),
    rsvpControl({
      event,
      counts: countsFromDetail(event),
      viewerZone,
      onChange,
      ...(event.isSuspended
        ? { frozenNote: 'RSVPs are paused while this group is suspended.' }
        : {}),
    }),
  ];

  if (event.isFull) rows.push(el('p', { class: 'notice' }, 'This event is full.'));
  if (event.closedAt) {
    rows.push(
      el('p', { class: 'muted small' }, 'The headcount is final. Photos can still be added.'),
    );
  }

  return [el('div', { class: 'stack-part' }, rows)];
}

/** Every field RsvpCounts needs is already on the detail payload. */
function countsFromDetail(event: EventDetail): RsvpCounts {
  return {
    eventId: event.eventId,
    yesCount: event.yesCount,
    dayofConfirmed: event.dayofConfirmed,
    maybeCount: event.maybeCount,
    noCount: event.noCount,
    totalResponses: event.yesCount + event.maybeCount + event.noCount,
    rsvpMin: event.rsvpMin,
    rsvpMax: event.rsvpMax,
    minMet: event.minMet,
    isFull: event.isFull,
    seatsRemaining: event.seatsRemaining,
    myRsvp: event.myStatus
      ? {
          status: event.myStatus,
          confirmedDayof: event.myConfirmedDayof,
          confirmedDate: event.myConfirmedDate,
        }
      : null,
  };
}

/**
 * Who said what.
 *
 * The server's ordering is the business rule — yes, then maybe, then no, with
 * day-of confirmed first inside each group — so the list is rendered in the order
 * it arrives and never re-sorted here.
 *
 * Each row shows the two facts separately: what they said in advance, and whether
 * they confirmed on the day. A yes with no confirmation is not an error state, it
 * is the signal the organizer is looking for.
 */
function rosterChildren(rsvps: RsvpEntry[], viewerZone: string): HTMLElement[] {
  if (rsvps.length === 0) {
    return [el('h2', {}, 'Responses'), el('p', { class: 'muted' }, 'Nobody has responded yet.')];
  }

  const rows = rsvps.map((r) =>
    el('tr', {}, [
      el('td', {}, r.name),
      el('td', {}, rsvpStatusLabel(r.status)),
      el(
        'td',
        {},
        r.confirmedDayof
          ? r.confirmedDate
            ? `✓ ${fmtInstant(r.confirmedDate, viewerZone, TimeFormat.INSTANT)}`
            : '✓'
          : '',
      ),
    ]),
  );

  return [
    el('h2', {}, `Responses (${rsvps.length})`),
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'table' }, [
        el('thead', {}, [
          el(
            'tr',
            {},
            ['Member', 'Said', 'Confirmed day-of'].map((h) => el('th', {}, h)),
          ),
        ]),
        el('tbody', {}, rows),
      ]),
    ]),
  ];
}

function winnersCard(event: EventDetail): HTMLElement {
  // Grouped by placement: several rows in one place are a chop of that place.
  // Older results (before places existed) are the same shape, a chop being every
  // row at placement 1, so they group and read correctly with no special case.
  const byPlace = new Map<number, typeof event.winners>();
  for (const w of event.winners) {
    byPlace.set(w.placement, [...(byPlace.get(w.placement) ?? []), w]);
  }
  const money = (c: number): string => `$${(c / 100).toFixed(2)}`;

  const rows = [...byPlace.entries()]
    .sort((x, y) => x[0] - y[0])
    .map(([place, group]) => {
      const names = group.map((w) => w.displayName).join(', ');
      const chop = group.length > 1 && group.every((w) => w.isChop);
      const paid = group.every((w) => w.payoutCents !== null);
      const total = group.reduce((n, w) => n + (w.payoutCents ?? 0), 0);
      const pct = group[0]?.payoutPct ?? null;
      const cuts = group.map((w) => w.payoutCents ?? 0);
      const money_ =
        paid && pct !== null
          ? ` · ${pct}% · ${money(total)}${chop ? ` (${cuts.map(money).join(', ')})` : ''}`
          : paid
            ? // An equal split carries no percentage, only each player's cut.
              ` · ${
                cuts.every((c) => c === cuts[0])
                  ? `${money(cuts[0] ?? 0)} each`
                  : cuts.map(money).join(', ')
              }`
            : '';
      return el('li', {}, `${placeLabel(event.eventType, place)}: ${names}${chop ? ' (chop)' : ''}${money_}`);
    });

  const children: HTMLElement[] = [el('h2', {}, 'Results'), el('ul', {}, rows)];
  if (event.potCents !== null && event.potCents > 0) {
    children.push(el('p', { class: 'muted' }, `Pot: ${money(event.potCents)}`));
  }
  if (event.highHand) {
    const hh = event.highHand;
    const bits = [hh.hand, hh.cents !== null ? money(hh.cents) : ''].filter(Boolean);
    children.push(
      el('p', {}, [
        el('strong', {}, 'High hand: '),
        `${hh.displayName}${bits.length ? ` · ${bits.join(' · ')}` : ''}`,
      ]),
    );
  }
  if (event.resultNotes) children.push(el('p', { class: 'muted' }, event.resultNotes));
  return el('section', { class: 'card' }, children);
}

function ordinal(n: number): string {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

// --- Organizer controls ---------------------------------------------------

function organizerCard(event: EventDetail, canCloseManually: boolean): HTMLElement {
  const children: HTMLElement[] = [
    el('h2', {}, 'Organizer'),
    el('p', {}, [el('a', { class: 'btn btn-link', href: `/events/${event.eventId}/edit` }, 'Edit event')]),
  ];

  if (event.closedAt) {
    children.push(
      el(
        'p',
        { class: 'muted' },
        event.closeReason === 'canceled'
          ? 'This event was canceled. It does not count towards anyone’s games played.'
          : 'This event is closed.',
      ),
    );
  } else if (!canCloseManually) {
    children.push(
      el(
        'p',
        { class: 'muted' },
        'This event type closes itself at its end time, so there is no Close button.',
      ),
    );
  } else {
    children.push(closePanel(event));
  }

  children.push(deleteButton(event));
  return el('section', { class: 'card' }, children);
}

/**
 * The close form. Winners are loaded lazily — the roster is only needed for a
 * `has_results` type, and fetching it for every Generic event would be a wasted
 * request on the most common path.
 *
 * ONE dropdown per player ("Not placed", "Chip leader", "Second chip leader"...)
 * instead of a tick list plus a Chop box. Two players on the same place IS a
 * chop; nothing else needs saying (Will, 2026-10-16: "if multiple people are
 * selected, then it's just assumed it's a chop"). The pot and the percentages sit
 * under it, with each player's amount shown as it is typed.
 *
 * `playerMode` is a player who RSVP'd yes finishing the game rather than an
 * organizer closing it: no Cancel, because the server refuses one from them.
 */
function closePanel(event: EventDetail, playerMode = false): HTMLElement {
  const def = eventType(event.eventType);
  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, playerMode ? 'Finish the game' : 'Close event');

  const reason = el('select', { id: 'close-reason', name: 'reason' }) as HTMLSelectElement;
  reason.append(el('option', { value: 'completed' }, 'Completed'));
  if (!playerMode) reason.append(el('option', { value: 'canceled' }, 'Canceled'));

  const winnerBlock = el('div', { class: 'winner-block' });
  const notes = el('textarea', { id: 'close-notes', name: 'notes', rows: 2 }) as HTMLTextAreaElement;
  const potInput = el('input', {
    id: 'close-pot',
    type: 'text',
    inputmode: 'decimal',
    placeholder: '0.00',
    autocomplete: 'off',
  }) as HTMLInputElement;
  const summary = el('div', { class: 'place-summary' });
  // High hand: a side pot for the best hand shown, separate from the main pot. A
  // winner is the only required part; the money and what the hand was are extras.
  const hhPlayer = el('select', { id: 'close-hh-player' }) as HTMLSelectElement;
  const hhHand = el('input', {
    id: 'close-hh-hand',
    type: 'text',
    maxlength: '100',
    placeholder: 'Four of a kind, nines',
    autocomplete: 'off',
  }) as HTMLInputElement;
  const hhAmount = el('input', {
    id: 'close-hh-amount',
    type: 'text',
    inputmode: 'decimal',
    placeholder: '0.00',
    autocomplete: 'off',
  }) as HTMLInputElement;
  const preview = el('p', { class: 'muted small' });

  /**
   * Equal is the default: everyone placed takes the same cut and finishing order
   * is only a record. Percent makes the order matter (Will, 2026-10-17: "I want
   * to have both").
   */
  let splitMode: 'equal' | 'percent' = 'equal';
  const splitBox = el('div', { class: 'split-mode', hidden: true });
  /** userId -> place number (1 = first). Absent means not placed. */
  const placeOf = new Map<number, number>();
  /** Percentages the organizer typed, by place number; absent = use the default. */
  const typedPct = new Map<number, number>();
  let roster: GroupMember[] = [];

  const nameOf = (id: number): string => {
    const m = roster.find((r) => r.userId === id);
    return m?.fullName || m?.email || `User ${id}`;
  };

  /**
   * Groups of tied players, in finishing order, each with the position it
   * STARTS at. Two players level for first are positions 1 and 2 between them,
   * so the next group starts at 3 (1, 1, 3), the usual ranking for a tie. A
   * skipped place does not leave a gap either.
   */
  function placesInUse(): { place: number; ids: number[] }[] {
    const used = [...new Set(placeOf.values())].sort((a, b) => a - b);
    let start = 1;
    return used.map((p) => {
      const ids = [...placeOf.entries()].filter(([, v]) => v === p).map(([id]) => id);
      const group = { place: start, ids };
      start += ids.length;
      return group;
    });
  }

  function potCents(): number | null {
    const raw = potInput.value.replace(/[$,\s]/g, '');
    if (raw === '') return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) return NaN;
    return Math.round(n * 100);
  }

  /**
   * The percentage shown for a group: what was typed, else the default split.
   * The default is the sum of the per-POSITION defaults the group covers, so a
   * tie pools the places it occupies: with three players placed, two level for
   * first take 50 + 30 = 80 between them (40 each) and the player behind takes
   * the 20, never more than a tied winner.
   */
  function pctFor(place: number, size: number, totalPlayers: number): number {
    const typed = typedPct.get(place);
    if (typed !== undefined) return typed;
    const row = DEFAULT_SPLITS[Math.min(totalPlayers, DEFAULT_SPLITS.length) - 1] ?? [];
    let pct = 0;
    for (let pos = place; pos < place + size; pos += 1) pct += row[pos - 1] ?? 0;
    return Math.round(pct * 100) / 100;
  }

  const totalPlaced = (): number => placeOf.size;

  const dollars = (cents: number): string => `$${(cents / 100).toFixed(2)}`;

  function renderSummary(): void {
    const groups = placesInUse();
    const pot = potCents();
    summary.replaceChildren();

    if (groups.length === 0) {
      preview.textContent = 'No winners: closing will not post anything.';
      return;
    }

    const first = groups[0]!;
    preview.textContent = `Will announce: ${winnersLabel(first.ids.map(nameOf), first.ids.length > 1)}`;

    const hasPot = pot !== null && !Number.isNaN(pot) && pot > 0;
    splitBox.hidden = !hasPot;
    if (!hasPot) return;
    renderSplitToggle();

    if (splitMode === 'equal') {
      const cuts = splitEven(pot, totalPlaced());
      let at = 0;
      for (const g of groups) {
        for (const id of g.ids) {
          summary.append(
            el('div', { class: 'place-row' }, [
              el('strong', {}, nameOf(id)),
              el('span', { class: 'small' }, dollars(cuts[at] ?? 0)),
            ]),
          );
          at += 1;
        }
      }
      summary.append(
        el('p', { class: 'small place-total' }, `Everyone placed splits ${dollars(pot)} equally.`),
      );
      return;
    }

    const pcts = groups.map((g) => pctFor(g.place, g.ids.length, totalPlaced()));
    const payouts = splitPot(
      pot,
      groups.map((g, i) => ({ pct: pcts[i] ?? 0, count: g.ids.length })),
    );

    const rows = groups.map((g, i) => {
      const pct = el('input', {
        type: 'number',
        class: 'place-pct',
        min: '0',
        max: '100',
        step: '0.01',
        value: String(pcts[i] ?? 0),
        'aria-label': `Percent for ${placeLabel(event.eventType, g.place)}`,
      }) as HTMLInputElement;
      pct.addEventListener('input', () => {
        typedPct.set(g.place, Number(pct.value) || 0);
        // Only the totals line and the amounts change, so the input keeps focus.
        refreshAmounts();
      });
      const amounts = el('span', { class: 'place-amounts small muted' });
      return { g, pct, amounts, row: el('div', { class: 'place-row' }, [
        el('strong', {}, placeLabel(event.eventType, g.place)),
        el(
          'span',
          { class: 'place-names small' },
          g.ids.map(nameOf).join(', ') +
            (g.ids.length > 1
              ? ` (chop: share the ${ordinal(g.place)} to ${ordinal(g.place + g.ids.length - 1)} place money)`
              : ''),
        ),
        el('label', { class: 'place-pct-label small' }, [pct, ' %']),
        amounts,
      ]) };
    });

    const totalLine = el('p', { class: 'small place-total' });

    function refreshAmounts(): void {
      const live = rows.map((r) => Number(r.pct.value) || 0);
      const out = splitPot(pot as number, rows.map((r, i) => ({ pct: live[i] ?? 0, count: r.g.ids.length })));
      rows.forEach((r, i) => {
        const each = out[i] ?? [];
        r.amounts.textContent =
          r.g.ids.length > 1
            ? `${dollars(each.reduce((a, b) => a + b, 0))} split: ${each.map(dollars).join(', ')}`
            : dollars(each[0] ?? 0);
      });
      const t = Math.round(live.reduce((a, b) => a + b, 0) * 100) / 100;
      totalLine.textContent =
        t === 100
          ? `Adds up to 100%. Pot ${dollars(pot as number)}.`
          : t < 100
            ? `Adds up to ${t}%, ${Math.round((100 - t) * 100) / 100}% short. It has to be 100.`
            : `Adds up to ${t}%, ${Math.round((t - 100) * 100) / 100}% over. It has to be 100.`;
      totalLine.classList.toggle('place-total-bad', t !== 100);
    }

    summary.append(...rows.map((r) => r.row), totalLine);
    refreshAmounts();
  }

  function renderSplitToggle(): void {
    const mk = (value: 'equal' | 'percent', label: string): HTMLElement => {
      const radio = el('input', { type: 'radio', name: 'split-mode', value }) as HTMLInputElement;
      radio.checked = splitMode === value;
      radio.addEventListener('change', () => {
        splitMode = value;
        renderSummary();
      });
      return el('label', { class: 'check' }, [radio, ` ${label}`]);
    };
    splitBox.replaceChildren(
      mk('equal', 'Split equally among everyone placed'),
      mk('percent', 'By percentage of the pot for each place'),
    );
  }

  function renderWinners(): void {
    // Canceled rejects winners server-side, so the picker goes away rather than
    // offering something that would 400.
    const show = def.hasResults && reason.value === 'completed';
    if (!show) {
      placeOf.clear();
      winnerBlock.replaceChildren();
      return;
    }

    const list = el('div', { class: 'winner-list' });
    for (const m of roster) {
      const pick = el('select', { id: `place-${m.userId}`, class: 'place-pick' }) as HTMLSelectElement;
      pick.append(el('option', { value: '0' }, 'Not placed'));
      for (let p = 1; p <= MAX_PLACES; p += 1) {
        pick.append(el('option', { value: String(p) }, placeLabel(event.eventType, p)));
      }
      pick.value = String(placeOf.get(m.userId) ?? 0);
      pick.addEventListener('change', () => {
        const v = Number(pick.value);
        if (v === 0) placeOf.delete(m.userId);
        else placeOf.set(m.userId, v);
        // The split depends on how many places there are, so a changed count
        // means the defaults apply again, not stale typed numbers.
        typedPct.clear();
        renderSummary();
      });
      list.append(
        el('label', { class: 'place-line', for: `place-${m.userId}` }, [
          el('span', {}, m.fullName || m.email || `User ${m.userId}`),
          pick,
        ]),
      );
    }

    winnerBlock.replaceChildren(
      el('h3', {}, 'Results'),
      el(
        'p',
        { class: 'muted small' },
        `Give each player who finished a place. Players in the same place chop: they split ` +
          'the money for every place they cover, and the next player is placed after them ' +
          '(two tied for first means the next is third). ' +
          'Leave everyone as Not placed to close without announcing anything.',
      ),
      list,
      el('label', { for: 'close-pot' }, 'Pot size ($)'),
      el('p', { class: 'muted small' }, 'Count up the pot at the end and enter the total. Leave blank for no payout.'),
      potInput,
      splitBox,
      summary,
      ...(event.eventType === 'poker'
        ? [
            el('h3', {}, 'High hand'),
            el(
              'p',
              { class: 'muted small' },
              'A side pot for the best hand shown. Optional: pick who had it, then the hand and the amount.',
            ),
            el('label', { for: 'close-hh-player' }, 'Who had it'),
            hhPlayer,
            el('label', { for: 'close-hh-hand' }, 'The hand'),
            hhHand,
            el('label', { for: 'close-hh-amount' }, 'Side pot ($)'),
            hhAmount,
          ]
        : []),
      el('label', { for: 'close-notes' }, 'Notes'),
      notes,
      preview,
    );
    renderHighHandChoices();
    renderSummary();
  }

  function renderHighHandChoices(): void {
    hhPlayer.replaceChildren(
      el('option', { value: '0' }, 'Nobody'),
      ...roster.map((m) => el('option', { value: String(m.userId) }, nameOf(m.userId))),
    );
  }

  potInput.addEventListener('input', () => {
    renderSummary();
  });
  reason.addEventListener('change', renderWinners);

  // Load the roster only for the type that can use it.
  if (def.hasResults) {
    void listMembers(event.groupId)
      .then((members) => {
        roster = members;
        renderWinners();
      })
      .catch(() => {
        winnerBlock.replaceChildren(
          el('p', { class: 'form-error' }, 'Could not load the roster, so winners cannot be picked.'),
        );
      });
  }

  const form = el('form', { method: 'post', action: `/events/${event.eventId}/close`, novalidate: true }, [
    el('h3', {}, playerMode ? 'Finish the game' : 'Close this event'),
    ...(playerMode
      ? []
      : [
          el('label', { for: 'close-reason' }, 'Reason'),
          reason,
          el(
            'p',
            { class: 'muted small' },
            'Canceled events are excluded from games played, so calling one off does not pad the leaderboard.',
          ),
        ]),
    winnerBlock,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;

    const groups = placesInUse();
    const pot = potCents();
    if (pot !== null && Number.isNaN(pot)) {
      error.textContent = 'The pot must be a dollar amount, like 120 or 120.50.';
      error.hidden = false;
      return;
    }
    const withPot = pot !== null && pot > 0 && groups.length > 0;
    let places: ClosePlace[] | undefined;
    if (groups.length > 0) {
      const pcts = groups.map((g) => pctFor(g.place, g.ids.length, totalPlaced()));
      if (withPot && splitMode === 'percent') {
        const total = Math.round(pcts.reduce((a, b) => a + b, 0) * 100) / 100;
        if (total !== 100) {
          error.textContent = `The percentages add up to ${total}%. They have to add up to 100.`;
          error.hidden = false;
          return;
        }
      }
      places = groups.map((g, i) => ({
        userIds: g.ids,
        ...(withPot && splitMode === 'percent' ? { pct: pcts[i] ?? 0 } : {}),
      }));
    }
    if (pot !== null && pot > 0 && groups.length === 0) {
      error.textContent = 'Pick who placed before entering a pot.';
      error.hidden = false;
      return;
    }

    // High hand: a winner is the one required part.
    const hhUser = Number(hhPlayer.value) || 0;
    const hhRaw = hhAmount.value.replace(/[$,\s]/g, '');
    const hhCents = hhRaw === '' ? null : Math.round(Number(hhRaw) * 100);
    if (hhCents !== null && (!Number.isFinite(hhCents) || hhCents < 0)) {
      error.textContent = 'The high-hand side pot must be a dollar amount.';
      error.hidden = false;
      return;
    }
    if (hhUser === 0 && (hhCents !== null || hhHand.value.trim() !== '')) {
      error.textContent = 'Pick who had the high hand, or clear the hand and the amount.';
      error.hidden = false;
      return;
    }

    const confirmMsg =
      reason.value === 'canceled'
        ? 'Cancel this event? RSVPs freeze and it will not count as a game played.'
        : groups.length === 0
          ? 'Close with no winners? Nothing will be announced.'
          : 'Close this event and announce the winners?';
    if (!window.confirm(confirmMsg)) return;

    submit.setAttribute('disabled', 'disabled');
    try {
      await closeEvent(event.eventId, {
        reason: reason.value as CloseReason,
        ...(places ? { places } : {}),
        ...(withPot ? { potCents: pot, split: splitMode } : {}),
        ...(hhUser > 0 && reason.value === 'completed'
          ? {
              highHand: {
                userId: hhUser,
                ...(hhCents !== null ? { cents: hhCents } : {}),
                ...(hhHand.value.trim() !== '' ? { hand: hhHand.value.trim() } : {}),
              },
            }
          : {}),
        notes: notes.value.trim() || undefined,
      });
      navigate(`/events/${event.eventId}`, true);
    } catch (err) {
      // A 409 means someone else closed it first — the stamp-first guard doing
      // its job. Reloading shows the real state rather than arguing about it.
      error.textContent =
        err instanceof ApiClientError && err.code === 'ALREADY_CLOSED'
          ? 'Someone else already closed this event. Reloading.'
          : err instanceof ApiClientError
            ? err.message
            : 'Could not close the event.';
      error.hidden = false;
      submit.removeAttribute('disabled');
      if (err instanceof ApiClientError && err.code === 'ALREADY_CLOSED') {
        navigate(`/events/${event.eventId}`, true);
      }
    }
  });

  return form;
}

const MAX_PLACES = 6;

/**
 * The split offered when a pot is entered and nobody has typed a percentage,
 * indexed by how many places are in use. The three-place row is Will's own
 * example (50 / 30 / 20); the rest follow the same shape and total 100.
 */
const DEFAULT_SPLITS: number[][] = [
  [100],
  [60, 40],
  [50, 30, 20],
  [40, 30, 20, 10],
  [40, 25, 20, 10, 5],
  [35, 25, 18, 12, 7, 3],
];

/**
 * What a place is called. Poker is decided by chip count, so it reads "chip
 * leader" there; every other results game keeps plain ordinals.
 */
function placeLabel(eventTypeKey: string, place: number): string {
  if (eventTypeKey === 'poker') {
    const names = ['Chip leader', 'Second chip leader', 'Third chip leader', 'Fourth chip leader', 'Fifth chip leader', 'Sixth chip leader'];
    return names[place - 1] ?? `${ordinal(place)} chip leader`;
  }
  return ordinal(place);
}

function deleteButton(event: EventDetail): HTMLElement {
  const btn = el('button', { class: 'btn btn-link btn-danger' }, 'Delete event');
  btn.addEventListener('click', async () => {
    if (!window.confirm(`Delete ${event.title}? RSVPs, photos, and results are kept.`)) return;
    btn.setAttribute('disabled', 'disabled');
    try {
      await deleteEvent(event.eventId);
      navigate(`/groups/${event.groupId}/events`, true);
    } catch (err) {
      window.alert(err instanceof ApiClientError ? err.message : 'Could not delete the event.');
      btn.removeAttribute('disabled');
    }
  });
  return btn;
}
